import { getProductUrlSlug, getProductVariant } from "../utils";
import Shopify, {
  ShopifyResponse,
  ShopifyProductResponse,
  ShopifyProductVariantResponse,
} from "../shopify";
import Log from "../Log";
import { Settings } from "../settings";
import OneSignal, { NotificationPermission } from "../types/OneSignal";
import { PromptManager } from "../prompt";
import DateHelper from "./dateHelper";

/** The number of months an in stock subscription is valid */
const IN_STOCK_VALID_MONTHS = 6;

interface SubscriptionTags{
  [tagName: string]: string;
}

interface State {
  subscriptionTags: SubscriptionTags;
  variantId: string | null;
  product: ShopifyProductResponse | null;
}

export default class InStockHelper {
  private static state: State = {
    product: null,
    variantId: null,
    subscriptionTags: {},
 };

  /**
   * Method that checks the conditions to display the in stock prompt:
   * * the settings should be present and the in stock alerts should be enabled on the UI
   * * the user is on a product page
   * * the product on the page is currently not available
   *
   * If those conditions are met then it sets up and triggers a display of the in stock prompt
   * using the given settings.
   *
   * @param shopifySettings The settings used to determine prompt content and feature enabled status
   */
  static async attemptToDisplayInStock(shopifySettings: Settings | undefined): Promise<void> {
    // If the settings failed or the in stock alerts weren't enabled
    // we won't display anything
    if (!(shopifySettings && shopifySettings.in_stock_alerts_enabled)) {
      Log.debug("Will not send in stock prompt: Alerts settings not enabled");
      return;
    }

    // Verify if we are on a product page that is out of stock
    const isProductUnavailable: boolean = await this.isProductOutOfStock();
    if (!isProductUnavailable) {
      Log.debug("Will not send in stock prompt: Is not an out of stock product page");
      return;
    }

    // Verify native push notification permissions are granted
    const permission = await (window.OneSignal as OneSignal).getNotificationPermission();
    if (permission === NotificationPermission.Denied) {
      Log.debug("Will not send in stock prompt: User did not grant native push permissions");
      return;
    }

    // Load current subscriptions
    await this.loadInStockTags();

    // Verify if the user doesn't already have an existing valid subscription to the product
    const existingSubscription: string = this.state.subscriptionTags[this.buildTagName()];
    const existingSubscriptionTimestamp: number = parseInt(existingSubscription);
    const todayTimestamp = DateHelper.getUnixTimestamp(new Date());
    if (
      existingSubscription &&
      !isNaN(existingSubscriptionTimestamp) &&
      todayTimestamp < existingSubscriptionTimestamp
    ) {
      Log.debug("Will not send in stock prompt: User already has a subscription for this product");
      return;
    }

    const onAllow = async (): Promise<void> => (
      await this.handleAllow(
        permission,
        shopifySettings.in_stock_alerts_config.confirmation_message
      )
    );
    await PromptManager.createInStockPrompt(shopifySettings.in_stock_alerts_config, onAllow);
  }

  /**
   * Handler for when the user accepts to receive in stock notifications on a product page.
   * If the user was not prompted yet on native push notifications it will be prompted now,
   * cancelling the action if the user denied permissions.
   *
   * When the user enables native permissions (or the permissions were already granted),
   * we save the user tags with the information we need and display a confirmation message
   * afterwards.
   *
   * @param permission The native prompt permissions used to determine if we ask or not
   * @param confirmationMessage The text used to display at the end of the flow on a prompt
   */
  private static async handleAllow(permission: NotificationPermission, confirmationMessage: string): Promise<void> {
    // If the user was not prompted for push notifications, attempt to subscribe
    if (permission === NotificationPermission.Default) {
      try {
        // This will trigger the native push notification prompt and wait until the user
        // does an action.
        await (window.OneSignal as OneSignal).registerForPushNotifications();
      } catch {
        // User denied permissions or closed the dialog
        Log.debug('In Stock Prompt: There was an error while subscribing to native prompts.');
      }
    }

    // Ask for permissions again afterwards to check the resulting action
    const newPermission = await (window.OneSignal as OneSignal).getNotificationPermission();

    // If the user did not accept native push do nothing else.
    if (newPermission !== NotificationPermission.Granted) {
      return;
    }

    await this.updateInStockTags();

    // Display a confirmation message
    await PromptManager.createConfirmationPrompt(confirmationMessage)
  }

  /**
   * Utility that determines if the user is on a page that
   * corresponds to a product out of stock.
   *
   * The availability is determined on a variant basis as the product could have stock, but
   * not on the variant the user is seeing.
   */
  private static async isProductOutOfStock(): Promise<boolean> {
    const productVariant: ShopifyProductVariantResponse | undefined = await this.getVariantInformation();
    return !!productVariant && !productVariant.available;
  }

  /**
   * Utility that uses the product name and variant id from the URL to retrieve the product information
   * using the Shopify API.
   * It will also set the resulting productId and variantId values for future use.
   *
   * @returns productVariant The variant information that corresponds to the product displayed on the page.
   */
  private static async getVariantInformation(): Promise<ShopifyProductVariantResponse | undefined> {
    const productHandle: string | undefined = getProductUrlSlug();
    if (!productHandle) {
      return undefined;
    }

    if (!this.state.product) {
      const response: ShopifyResponse<ShopifyProductResponse> = await Shopify.getProduct(productHandle);
      if (response.success) {
        this.state.product = response.body;
      } else {
        Log.error("Failed to query the product from the Shopify API.", response.error);
        return undefined;
      }
    }

    let productVariant: ShopifyProductVariantResponse | undefined;

    const variantId: string | null = getProductVariant();
    const variants = this.state.product.variants;

    if (variantId) {
      // If there is a variant present on the URL, we retrieve the variant info
      productVariant = variants.find(variant =>  `${variant.id}` === variantId);
    } else {
      // Return the first available variant or the first variant in the list if none are available
      const inStockVariant: ShopifyProductVariantResponse | undefined = variants.find(elem => elem.available);
      productVariant = inStockVariant ? inStockVariant : variants[0];
    }
    this.state.variantId = productVariant ? `${productVariant.id}` : null;

    return productVariant;
  }

  /**
   * Loads the saved player tags from the API. Won't perform the retrieval
   * if they are already saved on the helper state.
   */
  private static async loadInStockTags(): Promise<void> {
    if (Object.keys(this.state.subscriptionTags).length === 0) {
      // Retrieve the player tags from the API
      const savedTags = await (window.OneSignal as OneSignal).getTags() as SubscriptionTags;
      this.state.subscriptionTags = savedTags ? savedTags : {};
    }
  }

  /**
   * Builds the expiration timestamp for the in stock prompt subscription
   */
  private static buildSubscriptionExpirationTimestamp(): number {
    const date: Date = new Date();
    date.setMonth(date.getMonth() + IN_STOCK_VALID_MONTHS)
    return DateHelper.getUnixTimestamp(date);
  }

  /**
   * Builds the tag name for the in stock prompt subscription
   */
  private static buildTagName(): string {
    return `in_stock_${this.state.product?.id}_${this.state.variantId}`;
  }

  /**
   * Saves the tag when the user subscribes to an in stock prompt.
   * The tag has the form in_stock_[productId]_[variantId] = [timestamp_six_months_in_the_future]
   */
  private static async updateInStockTags(): Promise<void> {
    const tagName = this.buildTagName();
    const timestamp = this.buildSubscriptionExpirationTimestamp();

    await (window.OneSignal as OneSignal).sendTag(tagName, timestamp, (): void => {
      Log.debug("Updated in stock subscription successfully");
      this.state.subscriptionTags = {
        ...this.state.subscriptionTags,
        [tagName]: `${timestamp}`,
      };
    });
  }
}
